/**
 * Authenticated webmail frame (DESIGN.md T10): AppShell with the module switcher, folder tree
 * in the NavRail, mail search in the TopBar and the composer (T11, compose-host.tsx). The mail
 * page places the composer in its reading pane; other pages get it here.
 */
import { useI18n, type MessageKey } from '@platform/i18n';
import {
  adaptRouterLink,
  AppShell,
  BrandLogo,
  ContextualHelp,
  EditBoundary,
  ErrorState,
  FolderTree,
  Input,
  localizeRegistry,
  PromptDialog,
  resolveActions,
  Skeleton,
  toast,
  useAsync,
  useBranding,
  useErrorText,
  useEditBoundary,
  useShortcut,
  type ContextMenuSpec,
  type FolderTreeItem,
} from '@platform/ui';
import { ArchiveIcon, CalendarPlusIcon, FileTextIcon, InboxIcon, MailIcon, PencilIcon, SendIcon, SettingsIcon, ShieldAlertIcon, Trash2Icon, UploadIcon, type LucideIcon } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router';
import { createFolderActions } from './actions/folder-actions';
import { ImportMessagesDialog } from './import-messages';
import { api, type Folder, type SearchRequest, type WellKnownFolder } from './api';
import { useAuth } from './auth';
import { useComposeHost } from './compose-host';
import { useAfterIdle } from './use-after-idle';
import { MailContext, type MailContextValue, type SearchScope } from './mail-context';
import { MailSearchBar, type MailSearchBarHandle } from './search-bar';
import { useCalendarState } from './calendar/calendar-context';
import { CalendarProvider } from './calendar/calendar-provider';
import { CalendarRail } from './calendar/calendar-rail';
import { GroupwareRail } from './groupware/rail';
import { CalendarReminders } from './calendar/reminders';
import { webmailNavigation } from './navigation';
import { webmailHelpHref } from './help';
import { AutomaticRepliesBanner } from './automatic-replies';
import { useAutomaticReplies } from './automatic-replies-store';
import { RegionalProvider, RegionalUserMenu } from './regional-settings';
import { RegionalSetup } from './regional-setup';
import { folderMailboxName, folderParents, visibleFavourites, mailboxNavigationFolders } from './folder-navigation';
import { folderTransferPlan } from './folder-transfer';
import { SearchBox as CalendarSearchBox } from './calendar/event-search';
import { PublicFolderProvider } from './public-folder-context';
import { PublicFolderRail, PublicFolderShortcuts } from './pages/public-folders';
import type { MyPublicFolder } from '@platform/api-client';

const AppLink = adaptRouterLink(Link);

const WELL_KNOWN: Record<WellKnownFolder, { label: MessageKey; icon: LucideIcon }> = {
  inbox: { label: 'webmail.folders.inbox', icon: InboxIcon },
  drafts: { label: 'webmail.folders.drafts', icon: FileTextIcon },
  sent: { label: 'webmail.folders.sent', icon: SendIcon },
  outbox: { label: 'webmail.folders.outbox', icon: UploadIcon },
  archive: { label: 'webmail.folders.archive', icon: ArchiveIcon },
  junk: { label: 'webmail.folders.junk', icon: ShieldAlertIcon },
  deleted: { label: 'webmail.folders.deleted', icon: Trash2Icon },
};

/** Route of the mail page (it hosts the composer in its reading pane). */
const MAIL_PATH = '/';
const QUARANTINE_PATH = '/quarantine';

function readIdSet(key: string): Set<string> {
  try {
    const stored = window.localStorage.getItem(key);
    const parsed: unknown = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) && parsed.every((id) => typeof id === 'string') ? new Set(parsed) : new Set();
  } catch {
    return new Set();
  }
}

function writeIdSet(key: string, ids: ReadonlySet<string>) {
  try {
    window.localStorage.setItem(key, JSON.stringify([...ids]));
  } catch {
    // Browser storage is a convenience; navigation remains usable when it is unavailable.
  }
}

function readPublicPaths(key: string): Map<string, readonly string[]> {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(key) ?? '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return new Map();
    const entries: [string, unknown][] = Object.entries(parsed);
    const paths = entries.filter((entry): entry is [string, string[]] => {
      const [id, path] = entry;
      return Array.isArray(path) && path.length > 0 && path.length <= 16 && path.at(-1) === id && path.every((value: unknown) => typeof value === 'string' && value.length > 0 && value.length <= 1200);
    });
    return new Map<string, readonly string[]>(paths.slice(0, 50));
  } catch { return new Map(); }
}

export function WebmailLayout() {
  const { session } = useAuth();
  const { t } = useI18n();
  const confirmation = useMemo(() => ({
    title: t('webmail.calendar.editor.discardTitle'),
    description: t('webmail.calendar.editor.discardDescription'),
    confirmLabel: t('wizard.discard'),
  }), [t]);
  return <EditBoundary key={session?.username ?? 'signed-out'} confirmation={confirmation}>
    {() => <WebmailSessionLayout />}
  </EditBoundary>;
}

function WebmailSessionLayout() {
  const { t } = useI18n();
  const { instanceName } = useBranding();
  const errorText = useErrorText();
  const { session, logout } = useAuth();
  const edits = useEditBoundary();
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const folderId = params.get('folder') ?? 'inbox';
  const onCalendar = location.pathname.startsWith('/calendar');
  const onQuarantine = location.pathname === QUARANTINE_PATH;
  const onMail = location.pathname === MAIL_PATH;
  const onPublicFolders = location.pathname === '/folders';
  const [renderedMailSession, setRenderedMailSession] = useState<typeof session>();
  const renderToken = useMemo(() => ({}), [session, folderId, location.pathname]);
  const currentRenderToken = useRef(renderToken);
  useLayoutEffect(() => { currentRenderToken.current = renderToken; }, [renderToken]);
  const onInitialMailRendered = useCallback(() => {
    if (session && currentRenderToken.current === renderToken) setRenderedMailSession(session);
  }, [renderToken, session]);
  const [search, setSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchScope, setSearchScope] = useState<SearchScope>('folder');
  const [searchFilter, setSearchFilter] = useState<SearchRequest['filter']>({});
  const [searchPanelOpen, setSearchPanelOpen] = useState(false);
  const [quarantineFilters, setQuarantineFilters] = useState({ search: '', zone: 'all', from: '', to: '' });
  const [listVersion, setListVersion] = useState(0);
  const [mailDrag, setMailDrag] = useState<MailContextValue['mailDrag']>();
  const mailDropHandlerRef = useRef<MailContextValue['mailDropHandlerRef']['current']>(undefined);
  const [prompt, setPrompt] = useState<{ mode: 'create' | 'rename'; folder: Folder }>();
  const [importing, setImporting] = useState<Folder>();
  const collapsedStorageKey = `webmail.collapsedFolders.${session?.username ?? ''}`;
  const favouriteStorageKey = `webmail.favouriteFolders.${session?.username ?? ''}`;
  const sidebarStorageKey = `webmail.publicSidebarFolders.${session?.username ?? ''}`;
  const publicPathStorageKey = `webmail.publicFolderPaths.${session?.username ?? ''}`;
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(() => readIdSet(collapsedStorageKey));
  const [favouriteIds, setFavouriteIds] = useState<Set<string>>(() => readIdSet(favouriteStorageKey));
  const [sidebarIds, setSidebarIds] = useState<Set<string>>(() => readIdSet(sidebarStorageKey));
  const [publicPaths, setPublicPaths] = useState(() => readPublicPaths(publicPathStorageKey));
  const rememberPublicPath = useCallback((id: string, path: readonly string[]) => {
    if (!path.length || path.length > 16 || path.at(-1) !== id) return;
    setPublicPaths(current => {
      const previous = current.get(id);
      return previous?.length === path.length && path.every((part, index) => part === previous[index]) ? current : new Map(current).set(id, path);
    });
  }, []);
  const savedPublicPaths = useMemo(() => [...new Set([...favouriteIds, ...sidebarIds])].filter(id => id.startsWith('public:')).map(id => publicPaths.get(id.slice('public:'.length))).filter((path): path is readonly string[] => Boolean(path)).slice(0, 50), [favouriteIds, sidebarIds, publicPaths]);
  useEffect(() => {
    try { window.localStorage.setItem(publicPathStorageKey, JSON.stringify(Object.fromEntries(savedPublicPaths.map(path => [path.at(-1), path])))); }
    catch { /* Browser persistence is optional; fresh discovery remains authoritative. */ }
  }, [publicPathStorageKey, savedPublicPaths]);
  useEffect(() => writeIdSet(collapsedStorageKey, collapsedIds), [collapsedStorageKey, collapsedIds]);
  useEffect(() => writeIdSet(favouriteStorageKey, favouriteIds), [favouriteStorageKey, favouriteIds]);
  useEffect(() => writeIdSet(sidebarStorageKey, sidebarIds), [sidebarStorageKey, sidebarIds]);
  const searchRef = useRef<MailSearchBarHandle>(null);
  const calendarSearchRef = useRef<HTMLInputElement>(null);
  const quarantineSearchRef = useRef<HTMLInputElement>(null);
  const folders = useAsync(() => api.listFolders());
  // Essential folders, mail and preferences may load concurrently; optional work waits for the first committed page.
  const foldersLoaded = folders.data !== undefined;
  const secondaryLoad = Boolean(session) && (!onMail || (foldersLoaded && renderedMailSession === session));
  const idleAfterBoot = useAfterIdle(secondaryLoad);
  // The Quarantine entry shows only for users whose access profile grants any quarantine right.
  const quarantineAccess = useAsync(() => (session ? api.myQuarantinePermission() : Promise.resolve(undefined)), session?.username ?? '', secondaryLoad);
  const showQuarantine = Object.values(quarantineAccess.data?.zones ?? {}).some((level) => level !== 'none');
  const hasModuleSearch = onCalendar || onQuarantine || onMail;
  const calendarState = useCalendarState(Boolean(session) && onCalendar, Boolean(session));
  const host = useComposeHost({
    enabled: Boolean(session),
    preload: Boolean(session) && onMail && idleAfterBoot,
    displayTimeZone: calendarState.settings.displayTimeZone,
    onSent: () => {
      // Sent Items shows the new copy; folder counts change.
      setListVersion((version) => version + 1);
      folders.reload();
    },
  });

  const navigation = useMemo(() => localizeRegistry(webmailNavigation.map(group => ({ ...group, items: group.items.filter(item => item.status !== 'planned') })), t), [t]);
  const railNavigation = useMemo(
    () => navigation.map((group) => ({ ...group, items: group.items.filter((item) => item.id !== 'settings' && (item.id !== 'quarantine' || showQuarantine)) })).filter((group) => group.items.length > 0),
    [navigation, showQuarantine],
  );
  const automaticReplies = useAutomaticReplies(secondaryLoad);
  const hasSearchContext = searchPanelOpen || Boolean(search.trim() || searchQuery.trim()) || Object.keys(searchFilter ?? {}).length > 0;
  const focusSearch = () => onCalendar ? calendarSearchRef.current?.focus() : onQuarantine ? quarantineSearchRef.current?.focus() : searchRef.current?.focus();
  useShortcut('app.focusSearch', focusSearch, { enabled: Boolean(session) && hasModuleSearch });
  // The search bar shows Ctrl K as its hint, like the admin top bar; the palette moves to Ctrl Shift K.
  useShortcut('app.commandPalette', focusSearch, { enabled: Boolean(session) && hasModuleSearch });

  // Folders with the names the user sees (default folders localized).
  const shownFolders = useMemo<Folder[]>(
    () => mailboxNavigationFolders((folders.data ?? []).map((folder) => (folder.wellKnown ? { ...folder, name: t(WELL_KNOWN[folder.wellKnown].label) } : folder)), { name: session?.displayName || session?.username || '', address: session?.username || '' }),
    [folders.data, t, session?.displayName, session?.username],
  );
  const parents = useMemo(() => folderParents(shownFolders), [shownFolders]);
  const folderItems = useMemo<FolderTreeItem[]>(
    () =>
      shownFolders.map((folder) => ({
        id: folder.id,
        nativeId: folder.nativeId,
        name: folder.name,
        description: folder.mailboxAddress,
        icon: folder.mailboxRoot ? MailIcon : folder.wellKnown ? WELL_KNOWN[folder.wellKnown].icon : undefined,
        unread: folder.unread,
        depth: folder.depth,
        selectable: !folder.mailboxRoot,
        mailboxRoot: folder.mailboxRoot,
        parentId: parents.get(folder.id),
      })),
    [shownFolders, parents],
  );
  const favouriteFolders = useMemo(() => visibleFavourites(favouriteIds, shownFolders), [favouriteIds, shownFolders]);
  const favouriteFolderItems = useMemo(() => {
    const favourites = new Set(favouriteFolders.map((folder) => folder.id));
    return folderItems.filter((folder) => favourites.has(folder.id)).map((folder) => {
      const mailbox = folderMailboxName(folder.id, shownFolders, parents);
      return { ...folder, name: mailbox ? t('webmail.folders.sharedFavourite', { folder: folder.name, mailbox }) : folder.name, depth: 0, parentId: undefined };
    });
  }, [favouriteFolders, folderItems, shownFolders, parents, t]);

  if (!session) return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;

  const context: MailContextValue = {
    onInitialMailRendered,
    initialPreferencesReady: calendarState.settingsLoaded,
    quarantineFilters, setQuarantineFilters,
    folders: folders.data ?? [],
    folderItems,
    reloadFolders: folders.reload,
    folderId,
    search,
    searchQuery,
    clearSearchQuery: () => { setSearch(''); setSearchQuery(''); },
    searchScope,
    setSearchScope,
    searchFilter,
    setSearchFilter,
    searchPanelOpen,
    setSearchPanelOpen,
    selfAddresses: [session.username, ...host.identityEmails].filter((a) => a.includes('@')),
    compose: host.compose,
    composer: host.composer,
    composerLayout: host.composerLayout,
    leaveComposer: host.leaveComposer,
    reloadSignatures: host.reloadSignatures,
    listVersion,
    mailDrag,
    setMailDrag,
    mailDropHandlerRef,
  };

  const openFolder = (id: string) => {
    const select = () => {
      if (location.pathname === MAIL_PATH && id === folderId) {
        setListVersion(version => version + 1);
        return;
      }
      setSearch('');
      setSearchQuery('');
      if (location.pathname !== MAIL_PATH) void navigate(`/?folder=${encodeURIComponent(id)}`);
      else setParams({ folder: id });
    };
    if (location.pathname !== MAIL_PATH) host.leaveComposer(select);
    else select();
  };
  const toggleCollapsed = (id: string) => setCollapsedIds((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
  });
  const toggleFavourite = (folder: Folder) => setFavouriteIds((current) => {
    const next = new Set(current);
    if (next.has(folder.id)) next.delete(folder.id);
    else next.add(folder.id);
    return next;
  });
  const togglePublicId = (setter: typeof setFavouriteIds, folder: MyPublicFolder) => {
    setter((current) => {
      const next = new Set(current);
      const id = `public:${folder.id}`;
      if (!next.delete(id)) next.add(id);
      return next;
    });
  };
  const publicShortcutProps = {
    onToggleFavourite: (folder: MyPublicFolder) => togglePublicId(setFavouriteIds, folder),
    onToggleSidebar: (folder: MyPublicFolder) => togglePublicId(setSidebarIds, folder),
    onSelect: () => host.leaveComposer(() => void navigate('/folders')),
  };

  /**
   * Runs a folder call (undefined result = capability missing; those actions are "Planned" and
   * never run). On success: toast, refresh the tree and, if asked, the open list. Rethrows so the
   * prompt dialog stays open.
   */
  const folderCall = async (call: () => Promise<void> | undefined, toastText: string, refreshList = false) => {
    try {
      const pending = call();
      if (!pending) return;
      await pending;
    } catch (error) {
      errorText.notify(error);
      throw error;
    }
    toast.success(toastText);
    folders.reload();
    if (refreshList) setListVersion((version) => version + 1);
  };
  const quietly = (promise: Promise<void>) => promise.catch(() => undefined);

  const folderActions = createFolderActions(
    {
      create: (parent) => setPrompt({ mode: 'create', folder: parent }),
      rename: (folder) => setPrompt({ mode: 'rename', folder }),
      importMessages: (folder) => setImporting(folder),
      markAllRead: (folder) =>
        void quietly(folderCall(() => api.markAllRead?.(folder.id), t('webmail.folderActions.markedAllReadToast', { name: folder.name }), true)),
      empty: (folder) => quietly(folderCall(() => api.emptyFolder?.(folder.id), t('webmail.folderActions.emptiedToast', { name: folder.name }), true)),
      remove: async (folder) => {
        await quietly(folderCall(() => api.deleteFolder?.(folder.id), t('webmail.folderActions.deletedToast', { name: folder.name })));
        if (folder.id === folderId) openFolder('inbox');
      },
      isFavourite: (folder) => favouriteIds.has(folder.id),
      toggleFavourite,
    },
    {
      canCreate: Boolean(api.createFolder),
      canImport: Boolean(api.importMessage),
      canRename: Boolean(api.renameFolder),
      canDelete: Boolean(api.deleteFolder),
      canMarkAllRead: Boolean(api.markAllRead),
      canEmpty: Boolean(api.emptyFolder),
    },
  );
  const folderMenu = (id: string): ContextMenuSpec | undefined => {
    const folder = shownFolders.find((candidate) => candidate.id === id);
    return folder && !folder.mailboxRoot
      ? { title: folder.name, actions: resolveActions(folderActions, [folder], 'context') }
      : undefined;
  };

  const submitPrompt = (name: string) => {
    if (!prompt) return;
    const { mode, folder } = prompt;
    return mode === 'create'
      ? folderCall(() => api.createFolder?.(folder.id, name), t('webmail.folderActions.createdToast', { name }))
      : folderCall(() => api.renameFolder?.(folder.id, name), t('webmail.folderActions.renamedToast', { name }));
  };

  const mailDropProps = {
    canDropMailItem: (target: string) => Boolean(onMail && mailDrag && mailDropHandlerRef.current && folderTransferPlan(folderItems, mailDrag.sourceFolderIds).canTransfer(target)),
    onDropMailItem: (target: string, item: { id: string }, mode: 'move' | 'copy') => {
      if (item.id !== mailDrag?.item.id || !mailDropProps.canDropMailItem(target)) return;
      setMailDrag(undefined);
      mailDropHandlerRef.current?.(item.id, target, mode);
    },
  };
  const favouritesArea = (favouriteFolderItems.length > 0 || [...favouriteIds].some(id => id.startsWith('public:'))) && (
      <div>
      <FolderTree
        label={t('webmail.folders.favourites')}
        folders={favouriteFolderItems}
        activeId={location.pathname === MAIL_PATH ? folderId : undefined}
        unreadLabel={t('webmail.folders.unreadCount')}
        onSelect={openFolder}
        {...mailDropProps}
        folderMenu={folderMenu}
        folderActionsLabel={t('webmail.folderActions.more')}
      />
      <div className="px-4"><PublicFolderShortcuts {...publicShortcutProps} ids={favouriteIds} kind="favourites" /></div>
      </div>
    );
  const folderTree = folders.error ? (
    <ErrorState description={errorText.describe(folders.error)} onRetry={folders.reload} />
  ) : (
    <>
    {favouritesArea}
    <FolderTree
      label={t('webmail.folders.title')}
      folders={folderItems}
      activeId={location.pathname === MAIL_PATH ? folderId : undefined}
      unreadLabel={t('webmail.folders.unreadCount')}
      onSelect={openFolder}
      {...mailDropProps}
      folderMenu={folderMenu}
      folderActionsLabel={t('webmail.folderActions.more')}
      collapsedIds={collapsedIds}
      onToggleCollapsed={toggleCollapsed}
      collapseLabel={t('webmail.folderActions.collapse')}
      expandLabel={t('webmail.folderActions.expand')}
      header={
        <>
          {folders.loading && !folders.data && (
            <div className="flex flex-col gap-2 p-2" aria-busy="true">
              {Array.from({ length: 6 }, (_, index) => (
                <Skeleton key={index} className="h-4 w-3/4" />
              ))}
            </div>
          )}
        </>
      }
    />
    {sidebarIds.size > 0 && <div className="px-4"><p className="py-2 text-xs font-medium text-muted-foreground">{t('webmail.publicFolders.title')}</p><PublicFolderShortcuts {...publicShortcutProps} ids={sidebarIds} kind="sidebar" /></div>}
    </>
  );

  return (
    <RegionalProvider onSaved={(value) => calendarState.adoptSettings({ displayTimeZone: value.displayTimeZone, weekNumbering: value.weekNumbering })}>
    <PublicFolderProvider active={onPublicFolders || (onMail && secondaryLoad)} savedPaths={savedPublicPaths} onDiscoverPath={rememberPublicPath}>
    <MailContext.Provider value={context}>
      <CalendarProvider value={calendarState}>
        <CalendarReminders active={secondaryLoad} />
      <AppShell
        productName={instanceName}
        logo={<BrandLogo fallback={<MailIcon />} />}
        navigation={railNavigation}
        navigationVariant="icons"
        navigationPlacement="bottom"
        resizableRail
        actions={[{ id: 'open-settings', label: t('webmail.nav.settings'), icon: SettingsIcon, onSelect: () => host.leaveComposer(() => void navigate('/settings')) }]}
        activePath={location.pathname}
        linkComponent={AppLink}
        onNavigate={(path) => host.leaveComposer(() => void navigate(path))}
        onLinkNavigate={(path) => host.leaveComposer(() => void navigate(path))}
        primaryAction={
          host.composer || location.pathname === '/contacts' || location.pathname === '/tasks' ? undefined : onCalendar
            ? { id: 'new-event', label: t('webmail.calendar.newEvent'), icon: CalendarPlusIcon, onSelect: () => calendarState.openEditor({ mode: 'create' }) }
            : { id: 'new-mail', label: t('webmail.compose.newMessage'), icon: PencilIcon, onSelect: () => host.compose() }
        }
        searchOwnsCommandShortcut={hasModuleSearch}
        search={
          onCalendar ? <CalendarSearchBox id="calendar-shell-search" inputRef={calendarSearchRef} /> :
          onQuarantine ? <Input ref={quarantineSearchRef} type="search" className="max-w-md" aria-label={t('webmail.quarantine.search')} placeholder={t('webmail.quarantine.search')} value={quarantineFilters.search} onChange={(event) => setQuarantineFilters({ ...quarantineFilters, search: event.target.value })} /> : onMail ?
          <MailSearchBar
            ref={searchRef}
            value={search}
            onChange={setSearch}
            onSubmit={setSearchQuery}
            onClear={() => { setSearch(''); setSearchQuery(''); }}
            active={Boolean(searchQuery)}
            onMoreFilters={() => {
              if (location.pathname !== MAIL_PATH) void navigate('/');
              setSearchPanelOpen(true);
            }}
          /> : undefined
        }
        topBarEnd={
          <div className="flex items-center gap-1">
            <ContextualHelp href={webmailHelpHref(location.pathname, hasSearchContext)} label={t('common.help')} />
          </div>
        }
        userMenu={<RegionalUserMenu name={session.displayName} email={session.username} onSignOut={() => host.leaveComposer(() => edits?.request(() => void logout()))} settingsAction={{ label: t('webmail.nav.settings'), onSelect: () => host.leaveComposer(() => void navigate('/settings')) }} />}
        railContent={location.pathname === '/contacts' ? <GroupwareRail kind="contacts" /> : location.pathname === '/tasks' ? <GroupwareRail kind="tasks" /> : onCalendar ? <CalendarRail /> : onPublicFolders ? <>{favouritesArea}<PublicFolderRail {...publicShortcutProps} favouriteIds={favouriteIds} sidebarIds={sidebarIds} /></> : folderTree}
      >
        {location.pathname === MAIL_PATH ? (
          <div className="flex h-full min-h-0 flex-col">
            <AutomaticRepliesBanner replies={automaticReplies} />
            <div className="min-h-0 flex-1">
              <Outlet />
            </div>
          </div>
        ) : (
          <Outlet />
        )}
        {/* Pages without a reading pane: the composer is placed in the content area (full width covers it, T11). */}
        {location.pathname !== MAIL_PATH && host.composer}
        {host.dialog}
        <RegionalSetup />
        {importing && (
          <ImportMessagesDialog
            key={importing.id}
            folder={importing}
            onClose={() => setImporting(undefined)}
            onImported={() => {
              folders.reload();
              setListVersion((version) => version + 1);
            }}
          />
        )}
        {prompt && (
          <PromptDialog
            key={`${prompt.mode}-${prompt.folder.id}`}
            open
            onOpenChange={(value) => !value && setPrompt(undefined)}
            title={prompt.mode === 'create' ? t('webmail.folderActions.newFolderTitle', { parent: prompt.folder.name }) : t('webmail.folderActions.renameTitle')}
            label={t('webmail.folderActions.folderName')}
            requiredMessage={t('webmail.folderActions.nameRequired')}
            confirmLabel={t(prompt.mode === 'create' ? 'webmail.folderActions.create' : 'webmail.folderActions.rename')}
            initialValue={prompt.mode === 'rename' ? prompt.folder.name : ''}
            onSubmit={submitPrompt}
          />
        )}
      </AppShell>
      </CalendarProvider>
    </MailContext.Provider>
    </PublicFolderProvider>
    </RegionalProvider>
  );
}
